import {useEffect, useState} from "react";
import {useParams, useNavigate} from "react-router-dom";
import {App as AntdApp, Table, Tag} from "antd";
import className from "classnames/bind";
import {Button, FontAwesomeIcon, Loading, PageHeader, ProjectToolNav} from "~/components";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetProjectCrawlsQuery,
	useRunCrawlMutation,
	useDeleteCrawlMutation,
} from "~/reduxs/api/crawlerApiSlice";
import RunCrawlForm from "../components/RunCrawlForm";
import ScoreTrendChart from "../components/ScoreTrendChart";
import style from "../style/SiteCrawler.module.scss";

const cn = className.bind(style);

const STATUS_TAG = {
	running: {color: 'processing', text: 'Đang chạy'},
	done: {color: 'green', text: 'Hoàn tất'},
	failed: {color: 'red', text: 'Lỗi'},
};

/**
 * Trang tool Site Crawler của một dự án (`/projects/:id/crawler`): nút chạy crawl
 * (cấu hình per-lần), biểu đồ xu hướng điểm, và bảng lịch sử các lần crawl.
 * Tự poll khi có crawl đang chạy.
 */
function CrawlerHome() {

	const {id} = useParams();
	const navigate = useNavigate();
	const {notification, modal} = AntdApp.useApp();

	const [poll, setPoll] = useState(6000);
	const {data, isLoading} = useGetProjectCrawlsQuery(id, {
		pollingInterval: poll,
		skipPollingIfUnfocused: true,
	});

	const [runCrawl, {isLoading: running}] = useRunCrawlMutation();
	const [deleteCrawl] = useDeleteCrawlMutation();

	const [openRun, setOpenRun] = useState(false);

	const project = data?.project;
	const crawls = data?.crawls || [];
	const hasRunning = crawls.some((c) => c.status === 'running');
	const lastConfig = crawls[0]
		? {max_depth: crawls[0].max_depth, max_pages: crawls[0].max_pages, delay_ms: crawls[0].delay_ms, respect_robots: crawls[0].respect_robots}
		: undefined;

	useEffect(() => {
		setPoll(hasRunning ? 6000 : 0);
	}, [hasRunning]);

	const handleRun = async (config) => {
		try {
			const res = await runCrawl({projectId: id, ...config}).unwrap();
			notification.success({message: 'Đã bắt đầu', description: 'Crawl đang chạy nền. Theo dõi tiến độ trong báo cáo.'});
			setOpenRun(false);
			if (res?.crawl_id) navigate(`/crawler/crawls/${res.crawl_id}`);
		} catch (e) {
			notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Không thể bắt đầu crawl')});
		}
	};

	const handleDeleteCrawl = (crawl) => {
		modal.confirm({
			title: 'Xóa lần crawl',
			content: 'Xóa dữ liệu của lần crawl này?',
			okText: 'Xóa',
			okButtonProps: {danger: true},
			cancelText: 'Hủy',
			onOk: async () => {
				try {
					await deleteCrawl(crawl.id).unwrap();
					notification.success({message: 'Thành công', description: 'Đã xóa'});
				} catch (e) {
					notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Xóa thất bại')});
				}
			},
		});
	};

	if (isLoading) return <Loading />;
	if (!project) return <div className="container">Không tìm thấy dự án.</div>;

	const columns = [
		{
			title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 130,
			render: (s) => <Tag color={STATUS_TAG[s]?.color}>{STATUS_TAG[s]?.text || s}</Tag>,
		},
		{title: 'Số trang', dataIndex: 'pages_crawled', key: 'pages', width: 90},
		{
			title: 'Điểm TB', dataIndex: 'avg_score', key: 'avg', width: 90,
			render: (v, r) => (r.status === 'done' ? <strong>{v}</strong> : '—'),
		},
		{
			title: 'Cấu hình', key: 'config', width: 150,
			render: (_, r) => <span style={{fontSize: 12, color: '#6b7280'}}>depth {r.max_depth} · {r.max_pages} trang</span>,
		},
		{title: 'Bắt đầu', dataIndex: 'started_at', key: 'started', width: 160},
		{title: 'Kết thúc', dataIndex: 'finished_at', key: 'finished', width: 160, render: (v) => v || '—'},
		{
			title: '', key: 'actions', width: 100,
			render: (_, r) => (
				<div className={cn('tableActions')}>
					<Button small blue onClick={() => navigate(`/crawler/crawls/${r.id}`)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-chart-simple" />} />
					<Button small red onClick={() => handleDeleteCrawl(r)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />} />
				</div>
			),
		},
	];

	return (
		<div className="container">
			<ProjectToolNav projectId={id} active="crawler" />

			<PageHeader
				icon="fa-light fa-spider"
				title={`Site Crawler — ${project.name}`}
				subtitle={project.root_url}
				actions={
					<Button primary loading={running} disabled={hasRunning} onClick={() => setOpenRun(true)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-play" />}>
						{hasRunning ? 'Đang crawl…' : 'Chạy crawl'}
					</Button>
				}
			/>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Xu hướng điểm SEO qua các lần crawl</h3>
				<ScoreTrendChart crawls={crawls} />
			</div>

			<div className={cn('section')}>
				<h3 className={cn('sectionTitle')}>Lịch sử crawl</h3>
				<Table
					rowKey="id"
					columns={columns}
					dataSource={crawls}
					pagination={{pageSize: 10, hideOnSinglePage: true}}
				/>
			</div>

			<RunCrawlForm
				open={openRun}
				submitting={running}
				defaultValues={lastConfig}
				onCancel={() => setOpenRun(false)}
				onSubmit={handleRun}
			/>
		</div>
	);
}

export default CrawlerHome;
